/**
 * FikoMini3DDialog - Mini 3D viewer ile dialog component base class
 *
 * Dialog içinde FikoThreeJsComponent kullanarak tam özellikli 3D viewer.
 * Ana viewer ile aynı özelliklere sahip (WASM, GPU, LOD, vs.)
 *
 * NOT: Angular 19+ path alias sorunu nedeniyle dinamik import kullanılıyor
 * https://github.com/angular/angular/issues/59257
 *
 * KULLANIM:
 * ```typescript
 * @Component({...})
 * export class MyCustomDialog extends FikoMini3DDialogBase {
 *   override sahneHazir() {
 *     // Kendi 3D modelinizi yükleyin
 *   }
 * }
 * ```
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Directive,
  Injector,
  ViewContainerRef,
  EnvironmentInjector,
  Type,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoGPUAngleSearch, getGPUAngleSearch } from '../IFikoThreeJS/fiko-performance/FikoGPUAngleSearch';

/**
 * Mini 3D Event - FikoThreeJsComponent hazır olduğunda
 */
export interface FikoMini3DEvent {
  sahne: any; // FikoSahne
  kamera: THREE.Camera;
  threeJs: any; // FikoThreeJsComponent
}

/**
 * Base directive for dialog components with embedded 3D viewer
 * FikoThreeJsComponent kullanarak ana viewer ile aynı özelliklere sahip
 */
@Directive()
export abstract class FikoMini3DDialogBase implements OnInit, OnDestroy, AfterViewInit {
  // Dialog parametreleri (FikoDialogCore'dan gelir)
  @Input() pencere?: { instance: any; _refreshId: number };

  // 3D viewer ayarları
  @Input() viewerGenislik: number = 280;
  @Input() viewerYukseklik: number = 200;

  // Events
  @Output() kapatildi = new EventEmitter<void>();
  @Output() sahneHazirEvent = new EventEmitter<FikoMini3DEvent>();

  // Three.js referansları
  protected threeJs?: any;
  protected sahne?: any;
  protected kamera?: THREE.Camera;
  protected hazirMi = false;
  protected componentRef?: any;

  // Dialog boyut takibi için ResizeObserver
  private resizeObserver?: ResizeObserver;
  private dialogElement?: HTMLElement;
  private resizeDebounceTimer?: any;

  // PERFORMANS: Reusable objeler - dialogEkranaSigdir için
  private readonly _tempBox = new THREE.Box3();
  private readonly _tempCenter = new THREE.Vector3();
  private readonly _tempSize = new THREE.Vector3();

  constructor(
    protected cd: ChangeDetectorRef,
    protected injector: Injector,
    protected viewContainerRef: ViewContainerRef,
    protected environmentInjector: EnvironmentInjector,
    protected ngZone: NgZone
  ) {}

  ngOnInit(): void {
    // Override edilebilir
  }

  async ngAfterViewInit(): Promise<void> {
    // FikoThreeJsComponent'i lazy yükle
    await this.lazyLoadThreeJs();

    // Dialog boyut değişikliklerini izle
    this.setupResizeObserver();
  }

  /**
   * Dialog container boyut değişikliklerini izle
   * Dialog boyutu değiştiğinde 3D viewer'ı yeniden boyutlandır
   */
  private setupResizeObserver(): void {
    // Biraz bekle - DOM oluşsun
    setTimeout(() => {
      // viewer-container'ı bul
      const viewerContainer = this.viewContainerRef?.element?.nativeElement?.closest?.('.viewer-container')
                           || this.viewContainerRef?.element?.nativeElement?.parentElement;

      if (!viewerContainer) return;

      this.dialogElement = viewerContainer;

      // ResizeObserver ile boyut değişikliklerini izle - Angular zone dışında
      this.ngZone.runOutsideAngular(() => {
        this.resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            // Hemen yuvarlayarak ondalık farkları engelle
            const width = Math.round(entry.contentRect.width);
            const height = Math.round(entry.contentRect.height);
            if (width > 0 && height > 0) {
              this.onViewerBoyutDegisti(width, height);
            }
          }
        });

        this.resizeObserver.observe(viewerContainer);
      });
    }, 200);
  }

  /**
   * Viewer container boyutu değiştiğinde çağrılır (debounced)
   * ResizeObserver zone dışında çalıştığı için burada zone kontrolü yapılır
   */
  private onViewerBoyutDegisti(width: number, height: number): void {
    if (!this.threeJs || !this.hazirMi) return;

    // Yuvarlak değerler
    const roundedWidth = Math.round(width);
    const roundedHeight = Math.round(height);

    // Aynı değerler ise hiçbir şey yapma
    if (this.viewerGenislik === roundedWidth && this.viewerYukseklik === roundedHeight) {
      return;
    }

    // Sadece anlamlı değişikliklerde güncelle (5 piksel tolerance)
    if (Math.abs(this.viewerGenislik - roundedWidth) > 5 ||
        Math.abs(this.viewerYukseklik - roundedHeight) > 5) {

      // Debounce - yanıp sönmeyi engelle
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = setTimeout(() => {
        // Zone içinde güncelle - change detection için
        this.ngZone.run(() => {
          this.viewerGenislik = roundedWidth;
          this.viewerYukseklik = roundedHeight;
          this.fixRendererSize(this.threeJs);
          this.dialogEkranaSigdir();
        });
      }, 150); // 150ms debounce
    }
  }

  /**
   * FikoThreeJsComponent'i lazy yükle (Angular 19 path alias sorunu için)
   * https://github.com/angular/angular/issues/59257
   * Override edilebilir - farklı sahne tipi kullanmak için
   */
  protected async lazyLoadThreeJs(): Promise<void> {
    try {
      // Dinamik import - relative path kullan (path alias sorunu için)
      // NOT: Bu path fiko-mini-3d klasöründen fiko-three-js.component.ts'ye
      const { FikoThreeJsComponent } = await import('../fiko-three-js.component');
      // SahneStepViewer'ı da import et - tam özellikli sahne için
      const { SahneStepViewer } = await import('../Fiko3DComponent/sahne-step-viewer/SahneStepViewer');

      if (!FikoThreeJsComponent) {
        return;
      }

      // Component'i oluştur
      this.createThreeJsComponent(FikoThreeJsComponent, SahneStepViewer);
    } catch (error) {
    }
  }

  /**
   * FikoThreeJsComponent ve Sahne sınıfı ile component oluştur
   * Alt sınıflardan çağrılabilir
   */
  protected async loadWithCustomScene(SahneClass: any): Promise<void> {
    try {
      const { FikoThreeJsComponent } = await import('../fiko-three-js.component');

      if (!FikoThreeJsComponent) {
        return;
      }

      this.createThreeJsComponent(FikoThreeJsComponent, SahneClass);
    } catch (error) {
    }
  }

  /**
   * FikoThreeJsComponent'i oluştur ve ekle
   */
  protected createThreeJsComponent(componentClass: Type<any>, SahneClass?: any): void {
    if (!componentClass || !this.viewContainerRef) {
      return;
    }

    try {
      // Önce container'ı temizle
      this.viewContainerRef.clear();

      // Component'i oluştur
      this.componentRef = this.viewContainerRef.createComponent(componentClass, {
        environmentInjector: this.environmentInjector,
        injector: this.injector
      });

      // Input'ları ayarla
      const instance = this.componentRef.instance;
      if (instance) {
        instance.antiAlias = false;
        instance.enableShadows = true;
        instance.maxFPS = 60;
        instance.pixelRatio = 1.5;
        instance.enablePostProcessing = false;
        instance.enableFrustumCulling = true;
        instance.enableLOD = true;
      }

      // Boyut ayarla
      const hostElement = this.componentRef.location.nativeElement as HTMLElement;
      if (hostElement) {
        hostElement.style.width = `${this.viewerGenislik}px`;
        hostElement.style.height = `${this.viewerYukseklik}px`;
        hostElement.style.display = 'block';
        hostElement.style.position = 'relative';
      }

      // Change detection tetikle
      this.componentRef.changeDetectorRef.detectChanges();

      // ÖNEMLİ: FikoThreeJsComponent'in hazırlanmasını bekle ve sahne ekle
      // ngAfterViewInit FikoThreeJsComponent'te hazirla() çağırır
      // Biz de sahne oluşturup eklemeliyiz
      this.initializeScene(instance, SahneClass);

    } catch (error) {
    }
  }

  /**
   * Sahneyi initialize et - FikoThreeJsComponent hazır olduktan sonra
   */
  private initializeScene(instance: any, SahneClass?: any): void {
    // FikoThreeJsComponent'in canvas'ının DOM'a eklenmesini bekle
    let attempts = 0;
    const maxAttempts = 30;

    const checkCanvas = () => {
      attempts++;

      // Canvas hazır mı kontrol et
      if (instance?.canvas?.nativeElement && instance?.sunucu) {
        // Sahne oluştur ve ekle
        this.createAndAddScene(instance, SahneClass);
      } else if (attempts < maxAttempts) {
        setTimeout(checkCanvas, 100);
      }
    };

    // Kısa gecikme ile başla (ngAfterViewInit tamamlansın)
    setTimeout(checkCanvas, 50);
  }

  /**
   * Sahne oluştur ve sahneler array'ine ekle
   */
  private createAndAddScene(instance: any, SahneClass?: any): void {
    try {
      // SahneStepViewer varsa onu kullan, yoksa basit FikoSahne
      let sahne: any;

      if (SahneClass) {
        // SahneStepViewer - tam özellikli sahne (ışıklar, çizimler, vs.)
        sahne = new SahneClass(instance);
      } else {
        // Basit FikoSahne - minimal sahne
        // FikoSahne import edilmeli
        return;
      }

      // Sahneyi sahneler array'ine ekle
      instance.sahneler.push(sahne);

      // ÖNEMLİ: Renderer boyutunu dialog boyutuna göre ayarla
      // Dialog içinde oluşturulduğunda canvas boyutu yanlış olabiliyor
      this.fixRendererSize(instance);

      // İlk çizimi yap
      if (typeof instance.ciz === 'function') {
        instance.ciz();
      }

      // Şimdi hazır olmasını bekle
      this.waitForThreeJs(instance);

    } catch (error) {
    }
  }

  /**
   * Renderer boyutunu dialog boyutuna göre düzelt
   * Dialog içinde dinamik oluşturulduğunda boyut yanlış hesaplanabiliyor
   */
  protected fixRendererSize(instance: any): void {
    const width = this.viewerGenislik;
    const height = this.viewerYukseklik;

    // Host element boyutunu ayarla (parent container)
    if (this.componentRef?.location?.nativeElement) {
      const hostElement = this.componentRef.location.nativeElement as HTMLElement;
      hostElement.style.width = `${width}px`;
      hostElement.style.height = `${height}px`;
      hostElement.style.display = 'block';
      hostElement.style.position = 'relative';
      hostElement.style.overflow = 'hidden';
    }

    // Canvas element boyutunu ayarla - ÖNEMLİ: CSS boyutu ve actual boyutu ayrı
    if (instance.canvas?.nativeElement) {
      const canvas = instance.canvas.nativeElement as HTMLCanvasElement;
      // CSS boyutu
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      // Actual canvas boyutu (WebGL için)
      canvas.width = width * (instance.pixelRatio || 1);
      canvas.height = height * (instance.pixelRatio || 1);
    }

    // WebGL Renderer boyutunu ayarla - setSize parametresi updateStyle=false
    // Çünkü canvas style'ını yukarıda zaten ayarladık
    if (instance.sunucu) {
      instance.sunucu.setSize(width, height, false);
    }

    // CSS3D Renderer boyutunu ayarla
    if (instance.sunucuCSS3d) {
      instance.sunucuCSS3d.setSize(width, height);
    }

    // CSS2D Renderer boyutunu ayarla
    if (instance.sunucuCSS2d) {
      instance.sunucuCSS2d.setSize(width, height);
    }

    // Kamera aspect ratio'yu güncelle (Ortho kamera için)
    if (instance.aktifKamera) {
      instance.aktifKamera.left = width / -2;
      instance.aktifKamera.right = width / 2;
      instance.aktifKamera.top = height / 2;
      instance.aktifKamera.bottom = height / -2;
      instance.aktifKamera.updateProjectionMatrix();
    }

    // 🎯 KRİTİK FIX: FikoKontrolCad'in screen boyutlarını güncelle
    // Bu olmadan pan/rotate çalışmaz çünkü getMouseOnScreen/Circle yanlış hesaplar
    if (instance.aktifKontrol && typeof instance.aktifKontrol.handleResize === 'function') {
      instance.aktifKontrol.handleResize();
    }
  }

  /**
   * FikoThreeJsComponent hazır olana kadar bekle
   */
  private waitForThreeJs(instance: any): void {
    let attempts = 0;
    const maxAttempts = 50; // 5 saniye timeout

    const checkReady = () => {
      attempts++;

      if (instance?.sahneler?.[0]) {
        this.onThreeJsHazir(instance);
      } else if (attempts < maxAttempts) {
        setTimeout(checkReady, 100);
      }
    };

    setTimeout(checkReady, 200);
  }

  /**
   * FikoThreeJsComponent hazır olduğunda çağrılır
   */
  private onThreeJsHazir(instance: any): void {
    this.threeJs = instance;
    this.sahne = instance.sahneler?.[0];
    this.kamera = this.sahne?.kamera;
    this.hazirMi = true;

    // ÖNEMLİ: Boyutu son kez düzelt - ekranaSigdir'den önce doğru olmalı
    this.fixRendererSize(instance);

    // 🎯 KRİTİK FIX: DOM layout tamamlandıktan sonra kontrol screen'i tekrar güncelle
    // getBoundingClientRect doğru değer döndürmesi için layout'un bitmesi gerekir
    setTimeout(() => {
      if (instance.aktifKontrol && typeof instance.aktifKontrol.handleResize === 'function') {
        instance.aktifKontrol.handleResize();
      }
    }, 100);

    // Sahne hazır event'i
    if (this.sahne && this.threeJs) {
      this.sahneHazirEvent.emit({
        sahne: this.sahne,
        kamera: this.kamera,
        threeJs: this.threeJs
      });
    }

    // Alt sınıfın sahneHazir metodunu çağır
    this.sahneHazir();

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    // Zoom animasyonunu durdur
    if (this.zoomAnimasyon) {
      this.zoomAnimasyon.stop();
      this.zoomAnimasyon = null;
    }

    // Debounce timer temizle
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = undefined;
    }

    // ResizeObserver temizle
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    this.temizle();
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  /**
   * Sahne hazır olduğunda çağrılır - Alt sınıfta override edilmeli
   */
  protected sahneHazir(): void {
    // Alt sınıfta implement edilecek
  }

  /**
   * Temizlik - Alt sınıfta override edilebilir
   */
  protected temizle(): void {
    // Alt sınıfta override edilebilir
  }

  /**
   * Dialog'u kapat
   */
  kapat(): void {
    if (this.pencere?.instance?.kapa) {
      this.pencere.instance.kapa();
    }
    this.kapatildi.emit();
  }

  /**
   * Sahneyi yenile - sadece render, CD tetikleme yok
   */
  protected yenile(): void {
    this.threeJs?.ciz();
  }

  /**
   * Mesh ekle
   */
  protected meshEkle(mesh: THREE.Object3D): void {
    this.sahne?.add(mesh);
  }

  /**
   * Ekrana sığdır - Dialog boyutuna göre optimize edilmiş
   * FikoSahne.ekranaSigdir canvas.clientWidth kullanır, dialog içinde bu yanlış olabilir
   */
  protected ekranaSigdir(): void {
    if (!this.threeJs || !this.sahne) return;

    // Önce boyutu son kez düzelt
    this.fixRendererSize(this.threeJs);

    // Kısa gecikme ile ekrana sığdır (DOM güncellemesi için)
    setTimeout(() => {
      // Dialog boyutlarını kullanarak özel ekrana sığdırma
      this.dialogEkranaSigdir();
    }, 100);
  }

  // Animasyon için TWEEN referansı
  private zoomAnimasyon: any = null;
  private sonHedefZoom: number = 1;

  /**
   * Dialog boyutlarına göre özel ekrana sığdırma (animasyonlu)
   * FikoSahne.ekranaSigdir'in canvas.clientWidth kullanmasını bypass eder
   * PERFORMANS: Reusable objeler kullanır - allocation yok
   */
  protected dialogEkranaSigdir(): void {
    const sahne = this.threeJs?.sahneler?.[0];
    if (!sahne) return;

    // PERFORMANS: Reusable Box3 - reset et ve tekrar kullan
    this._tempBox.makeEmpty();
    let hasMesh = false;

    sahne.traverse((obj: any) => {
      if (obj instanceof THREE.Mesh && obj.geometry) {
        this._tempBox.expandByObject(obj);
        hasMesh = true;
      }
    });

    if (!hasMesh) {
      return;
    }

    // PERFORMANS: Reusable Vector3'ler - allocation yok
    this._tempBox.getCenter(this._tempCenter);
    this._tempBox.getSize(this._tempSize);
    const maxDim = Math.max(this._tempSize.x, this._tempSize.y, this._tempSize.z);

    if (maxDim === 0) return;

    // Kamerayı merkeze bak ve zoom ayarla
    const kamera = this.threeJs.aktifKamera as THREE.OrthographicCamera;
    const kontrol = this.threeJs.aktifKontrol;

    if (kamera && kontrol) {
      // Viewer boyutları (piksel)
      const viewWidth = this.viewerGenislik || 300;
      const viewHeight = this.viewerYukseklik || 200;

      // Padding faktörü - modelin etrafında biraz boşluk bırak
      const padding = 1.2; // %20 padding

      // Model boyutları - tüm eksenlerin maksimumunu al
      // Çünkü kamera herhangi bir açıdan bakıyor olabilir
      const modelMaxDim = maxDim * padding;

      // Ortografik kamera için zoom hesabı:
      // frustum genişliği = (right - left) / zoom
      // Model'in frustum'a sığması için: modelMaxDim <= frustumWidth / zoom
      // yani: zoom <= frustumWidth / modelMaxDim
      //
      // fixRendererSize'da frustum şöyle ayarlandı:
      // left = -viewWidth/2, right = viewWidth/2 → frustumWidth = viewWidth
      // top = viewHeight/2, bottom = -viewHeight/2 → frustumHeight = viewHeight
      //
      // Zoom = 1 iken 1 birim = 1 piksel
      // Model'i sığdırmak için: zoom = viewSize / modelSize

      const zoomX = viewWidth / modelMaxDim;
      const zoomY = viewHeight / modelMaxDim;
      const hedefZoom = Math.min(zoomX, zoomY) * 0.85; // %85 - kenar boşluğu


      // Eğer hedef zoom değişmediyse animasyon yapma
      if (Math.abs(this.sonHedefZoom - hedefZoom) < 0.01) {
        return;
      }
      this.sonHedefZoom = hedefZoom;

      // Önceki animasyonu durdur
      if (this.zoomAnimasyon) {
        this.zoomAnimasyon.stop();
      }

      // Animasyon parametreleri
      const baslangicZoom = kamera.zoom;
      const baslangicTarget = kontrol.target.clone();
      const animasyon = { progress: 0 };

      // TWEEN ile animasyonlu geçiş
      const TWEEN = (window as any).TWEEN;
      if (TWEEN) {
        this.zoomAnimasyon = new TWEEN.Tween(animasyon)
          .to({ progress: 1 }, 400) // 400ms animasyon
          .easing(TWEEN.Easing.Quadratic.Out)
          .onUpdate(() => {
            // Zoom interpolasyonu
            kamera.zoom = baslangicZoom + (hedefZoom - baslangicZoom) * animasyon.progress;
            kamera.updateProjectionMatrix();

            // Target interpolasyonu - _tempCenter kullan
            kontrol.target.lerpVectors(baslangicTarget, this._tempCenter, animasyon.progress);
            kontrol.update();
          })
          .onComplete(() => {
            this.zoomAnimasyon = null;
          })
          .start();

        // Animasyon süresince çizim yap
        this.threeJs?.surekliCiz?.();
        setTimeout(() => {
          this.threeJs?.surekliCizIptal?.();
        }, 450);
      } else {
        // TWEEN yoksa direkt uygula - _tempCenter kullan
        kontrol.target.copy(this._tempCenter);
        kamera.zoom = hedefZoom;
        kamera.updateProjectionMatrix();
        kontrol.update();
        this.threeJs.ciz?.();
      }
    }
  }

  /**
   * Kamera pozisyonu ayarla
   */
  protected kameraPozisyonAyarla(x: number, y: number, z: number): void {
    if (this.kamera) {
      this.kamera.position.set(x, y, z);
      this.kamera.lookAt(0, 0, 0);
    }
  }
}

/**
 * FikoYT Diagonal Kafa dialog component
 * I, J, K değerleri ve açı slider'ları ile 3D önizleme
 * FikoThreeJsComponent kullanarak ana viewer ile aynı özelliklere sahip
 */
@Component({
  selector: 'fikoyt-koc-kafa-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="koc-kafa-container">
      <!-- 3D Viewer Container - FikoThreeJsComponent buraya dinamik olarak eklenir -->
      <div class="viewer-container" #viewerContainer>
        <ng-container #threeJsContainer></ng-container>
        <!-- Konuma Sıfırla Butonu -->
        <button class="home-btn" (click)="kameraSifirla()" title="Başlangıç Konumuna Git">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
        </button>
        @if (!hazirMi) {
          <div class="loading-overlay">
            <div class="loader"></div>
            <span>3D Yükleniyor...</span>
          </div>
        }
      </div>

      <!-- Kontrol Paneli -->
      <div class="kontrol-panel">
        <!-- I, J, K Değerleri -->
        <div class="deger-satiri">
          <label>I:</label>
          <div class="deger-grup">
            <input type="number" class="ayar-input" [(ngModel)]="iDeger" (ngModelChange)="degerDegisti()" step="0.1">
            <button class="mini-btn" (click)="degerAl('I')"><i class="crosshairs icon"></i></button>
          </div>
        </div>

        <div class="deger-satiri">
          <label>J:</label>
          <div class="deger-grup">
            <input type="number" class="ayar-input" [(ngModel)]="jDeger" (ngModelChange)="degerDegisti()" step="0.1">
            <button class="mini-btn" (click)="degerAl('J')"><i class="crosshairs icon"></i></button>
          </div>
        </div>

        <div class="deger-satiri">
          <label>K:</label>
          <div class="deger-grup">
            <input type="number" class="ayar-input" [(ngModel)]="kDeger" (ngModelChange)="degerDegisti()" step="0.1">
            <button class="mini-btn" (click)="degerAl('K')"><i class="crosshairs icon"></i></button>
          </div>
        </div>

        <!-- B Açısı Slider -->
        <div class="slider-satiri">
          <div class="slider-baslik">
            <span>B Açısı:</span>
            <span>{{ bAcisi?.toFixed(3) || '0.000' }}°</span>
          </div>
          <input type="range"
                 class="slider cyan"
                 [(ngModel)]="bAcisi"
                 (ngModelChange)="aciDegisti()"
                 min="0" max="360" step="0.001">
        </div>

        <!-- C Açısı Slider -->
        <div class="slider-satiri">
          <div class="slider-baslik">
            <span>C Açısı:</span>
            <span>{{ cAcisi?.toFixed(3) || '0.000' }}°</span>
          </div>
          <input type="range"
                 class="slider green"
                 [(ngModel)]="cAcisi"
                 (ngModelChange)="aciDegisti()"
                 min="0" max="360" step="0.001">
        </div>

        <!-- Açı Sapması -->
        <div class="sapma-satiri">
          <span>Açı Sapması: {{ aciSapmasi?.toFixed(3) || '0.000' }}</span>
        </div>

        <!-- Butonlar -->
        <div class="buton-satiri">
          <button class="ui mini blue button aciyibul-btn"
                  [class.loading]="aciHesaplaniyor"
                  [disabled]="aciHesaplaniyor"
                  (click)="aciyiBul()">
            @if (aciHesaplaniyor) {
              <span class="btn-spinner"></span>
              <span>Hesaplanıyor...</span>
            } @else {
              <span>Açıyı Bul</span>
            }
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }

    .koc-kafa-container {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 4px;
      background: rgba(0, 64, 64, 0.8);
      border-radius: 6px;
      min-width: 200px;
      width: 100%;
      max-width: 320px;
      height: 100%;
      box-sizing: border-box;
    }

    .viewer-container {
      position: relative;
      border-radius: 4px;
      overflow: hidden;
      border: 1px solid rgba(0, 255, 255, 0.3);
      background: #0a1628;
      flex: 1;
      min-height: 100px;
      /* Touch cihazlarda pan/zoom için gerekli */
      touch-action: none;
    }

    .home-btn {
      position: absolute;
      bottom: 6px;
      right: 6px;
      width: 24px;
      height: 24px;
      padding: 0;
      background: rgba(0, 40, 60, 0.85);
      border: 1px solid rgba(255, 215, 0, 0.4);
      border-radius: 4px;
      color: #FFD700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      z-index: 5;
    }

    .home-btn:hover {
      background: rgba(255, 215, 0, 0.2);
      border-color: #FFD700;
      box-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
      transform: scale(1.1);
    }

    .home-btn:active {
      transform: scale(0.95);
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: rgba(10, 22, 40, 0.9);
      color: #00ffff;
      font-size: 9px;
      z-index: 10;
    }

    .loader {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(0, 255, 255, 0.3);
      border-top-color: #00ffff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .kontrol-panel {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      padding: 0.3rem;
      background: rgba(0, 20, 30, 0.5);
      border-radius: 2px;
      border: 1px solid rgba(0, 200, 255, 0.15);
    }

    .deger-satiri {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.2rem;
      margin-bottom: 0.15rem;
      padding-bottom: 0.1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .deger-satiri:last-of-type {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .deger-satiri label {
      color: #00ccff;
      font-weight: 500;
      font-size: 10px;
      min-width: 14px;
      flex-shrink: 0;
    }

    .deger-grup {
      display: flex;
      align-items: center;
      gap: 0.15rem;
      flex: 1;
      min-width: 0;
    }

    .deger-satiri input[type="number"],
    .ayar-input {
      flex: 1;
      background: rgba(0, 50, 70, 0.6);
      border: 1px solid rgba(0, 200, 255, 0.3);
      color: #00ff88;
      padding: 0.15rem 0.2rem;
      font-size: 10px;
      border-radius: 2px;
      outline: none;
      transition: all 0.2s ease;
      max-width: 100%;

      /* Chrome, Safari, Edge, Opera */
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox */
      -moz-appearance: textfield;
    }

    .deger-satiri input[type="number"]:focus,
    .ayar-input:focus {
      border-color: #00ccff;
      box-shadow: 0 0 3px rgba(0, 200, 255, 0.3);
    }

    .mini-btn {
      background: rgba(255, 215, 0, 0.1);
      border: 1px solid rgba(255, 215, 0, 0.3);
      color: #FFD700;
      padding: 0.15rem 0.25rem;
      border-radius: 2px;
      cursor: pointer;
      transition: all 0.2s;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
    }

    .mini-btn:hover {
      background: rgba(255, 215, 0, 0.2);
      border-color: #FFD700;
      box-shadow: 0 0 4px rgba(255, 215, 0, 0.3);
    }

    .slider-satiri {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      margin-bottom: 0.2rem;
    }

    .slider-baslik {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #FFD700;
      font-size: 10px;
      font-weight: 500;
    }

    .slider-baslik span:last-child {
      color: #00ff88;
      font-weight: 600;
      font-size: 10px;
    }

    .slider {
      width: 100%;
      height: 3px;
      border-radius: 2px;
      outline: none;
      -webkit-appearance: none;
      appearance: none;
      padding: 0;
    }

    .slider.cyan {
      background: linear-gradient(to right, rgba(0, 255, 255, 0.2), #00ffff);
    }

    .slider.green {
      background: linear-gradient(to right, rgba(0, 255, 0, 0.2), #00ff00);
    }

    .slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #FFD700;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(255, 215, 0, 0.5);
    }

    .slider::-moz-range-thumb {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #FFD700;
      cursor: pointer;
      border: none;
      box-shadow: 0 0 2px rgba(255, 215, 0, 0.5);
    }

    .sapma-satiri {
      text-align: center;
      color: #FFD700;
      font-size: 10px;
      font-weight: 500;
      padding: 0.2rem 0.3rem;
      background: rgba(255, 215, 0, 0.1);
      border: 1px solid rgba(255, 215, 0, 0.2);
      border-radius: 2px;
    }

    .buton-satiri {
      display: flex;
      gap: 0.15rem;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-top: 0.2rem;
      padding-top: 0.2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .buton-satiri button {
      font-size: 9px !important;
      padding: 0.2rem 0.3rem !important;
      background-color: rgba(255, 215, 0, 0.1) !important;
      border: 1px solid rgba(255, 215, 0, 0.3) !important;
      color: #FFD700 !important;
      border-radius: 2px !important;
      height: 16px;
      transition: all 0.2s ease;
    }

    .buton-satiri button:hover {
      background-color: rgba(255, 215, 0, 0.2) !important;
      border-color: #FFD700 !important;
      box-shadow: 0 0 4px rgba(255, 215, 0, 0.3);
    }

    .buton-satiri button.blue {
      background-color: rgba(0, 120, 200, 0.3) !important;
      border-color: rgba(0, 150, 255, 0.5) !important;
      color: #00ccff !important;
    }

    .buton-satiri button.blue:hover {
      background-color: rgba(0, 150, 255, 0.4) !important;
      border-color: #00ccff !important;
      box-shadow: 0 0 4px rgba(0, 200, 255, 0.4);
    }

    /* Açıyı Bul butonu loading animasyonu */
    .aciyibul-btn {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 3px;
      min-width: 55px;
      transition: all 0.3s ease;
    }

    .aciyibul-btn.loading {
      background: linear-gradient(90deg,
        rgba(0, 150, 255, 0.3) 0%,
        rgba(0, 200, 255, 0.5) 50%,
        rgba(0, 150, 255, 0.3) 100%
      ) !important;
      background-size: 200% 100% !important;
      animation: loading-shimmer 1.5s ease-in-out infinite;
      cursor: wait !important;
    }

    .aciyibul-btn:disabled {
      opacity: 0.9 !important;
    }

    .btn-spinner {
      width: 8px;
      height: 8px;
      border: 1px solid rgba(0, 200, 255, 0.3);
      border-top-color: #00ccff;
      border-radius: 50%;
      animation: spinner-rotate 0.8s linear infinite;
      flex-shrink: 0;
    }

    @keyframes spinner-rotate {
      to { transform: rotate(360deg); }
    }

    @keyframes loading-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `]
})
export class FikoKocKafaDialogComponent extends FikoMini3DDialogBase {
  @ViewChild('threeJsContainer', { read: ViewContainerRef, static: true })
  override viewContainerRef!: ViewContainerRef;

  @ViewChild('viewerContainer', { static: true })
  viewerContainerRef!: ElementRef<HTMLDivElement>;

  // Değerler
  iDeger: number = 0;
  jDeger: number = 0;
  kDeger: number = 1;

  // Açılar
  bAcisi: number = 0;
  cAcisi: number = 0;
  aciSapmasi: number = 0;

  // Açı bulma algoritması parametreleri
  aciHesaplaniyor: boolean = false;
  // PERFORMANS: GPU ile aynı hassasiyet - açı sapması 0 olana kadar ara
  farkTolerans: number = 1e-6; // ~0.000001° hassasiyet
  aciBulmaAdimi: number = 0.01;

  // PERFORMANS: Reusable objeler - farkHesaplaSessiz için
  private readonly _tempCWorldDir = new THREE.Vector3();
  private readonly _tempHedefYon = new THREE.Vector3();

  // WTEC Kafa sahne referansı
  private wtecSahne?: any;

  // WebGPU Açı Arama - 100x hızlı paralel hesaplama
  private gpuAngleSearch: FikoGPUAngleSearch;

  constructor(
    cd: ChangeDetectorRef,
    injector: Injector,
    viewContainerRef: ViewContainerRef,
    environmentInjector: EnvironmentInjector,
    ngZone: NgZone
  ) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 200;
    this.viewerYukseklik = 180;

    // WebGPU açı arama singleton'ını al
    this.gpuAngleSearch = getGPUAngleSearch();
  }

  /**
   * Override: SahneWtecKafa kullan
   */
  protected override async lazyLoadThreeJs(): Promise<void> {
    try {
      const { FikoThreeJsComponent } = await import('../fiko-three-js.component');
      const { SahneWtecKafa } = await import('../Fiko3DComponent/sahne-kafa/SahneWtecKafa');

      if (!FikoThreeJsComponent) {
        return;
      }

      this.createThreeJsComponent(FikoThreeJsComponent, SahneWtecKafa);
    } catch (error) {
    }
  }

  override sahneHazir(): void {
    if (!this.sahne) return;

    // SahneWtecKafa referansını sakla
    this.wtecSahne = this.sahne;

    // B ve C açı parametrelerini bağla
    this.bindAciParametreleri();

    // Model yüklenene kadar bekle ve ekrana sığdır
    // WTEC Kafa modeli async yükleniyor (FikoImporterWRL.alUrlden)
    this.waitForModelAndFit();

    // Başlangıç açı sapmasını hesapla (I=0, J=0, K=1 için)
    setTimeout(() => {
      this.guncelleAciSapmasi();
      this.cd.detectChanges();
    }, 500);
  }

  /**
   * Model yüklenene kadar bekle, sonra ekrana sığdır
   * Mesh sayısı artmayı durdurunca model yüklenmiş demektir
   */
  private waitForModelAndFit(): void {
    let lastMeshCount = 0;
    let stableCount = 0;
    let attempts = 0;
    const maxAttempts = 30; // 6 saniye max

    const checkMeshes = () => {
      attempts++;

      // Mevcut mesh sayısını say
      let currentMeshCount = 0;
      this.sahne?.traverse((obj: any) => {
        if (obj instanceof THREE.Mesh) currentMeshCount++;
      });

      // Mesh sayısı değişmiyorsa model yüklenmiş demektir
      if (currentMeshCount > 0 && currentMeshCount === lastMeshCount) {
        stableCount++;
        if (stableCount >= 3) {
          // 3 kez stabil = model yüklendi
          // Model yüklendi

          // Sabit kamera pozisyonuna animasyonlu git
          this.kameraSabitPozisyonaGit();
          this.yenile();
          return;
        }
      } else {
        stableCount = 0;
        lastMeshCount = currentMeshCount;
      }

      // Devam et
      if (attempts < maxAttempts) {
        setTimeout(checkMeshes, 200);
      } else {
        // Timeout - ne varsa onu sığdır
        // Model yükleme timeout
        this.ekranaSigdir();
        this.yenile();
      }
    };

    // İlk kontrol için biraz bekle
    setTimeout(checkMeshes, 300);
  }

  /**
   * Kamerayı sabit pozisyona animasyonlu götür (Fikonum.1 konumu)
   */
  private kameraSabitPozisyonaGit(): void {
    const kamera = this.threeJs?.aktifKamera;
    const kontrol = this.threeJs?.aktifKontrol;
    const kaynakca = this.wtecSahne?.kaynakca;

    if (!kamera || !kontrol || !kaynakca) {
      return;
    }

    // Fikonum.1 konumuna git (kaynakca.konumlar üzerinden)
    if (kaynakca.konumlar?.git) {
      kaynakca.konumlar.git('Fikonum.1');
    }
  }

  /**
   * Kamerayı başlangıç konumuna sıfırla (Home butonu için)
   */
  kameraSifirla(): void {
    this.kameraSabitPozisyonaGit();
    this.yenile();
  }

  /**
   * WTEC Kafa B/C açı parametrelerini UI'a bağla
   */
  private bindAciParametreleri(): void {
    if (!this.wtecSahne) return;

    try {
      // B açısı parametresini oku
      const bAcisiParam = this.wtecSahne.BAcisi?.userData?.['FikoParametreler']?.[0];
      if (bAcisiParam) {
        this.bAcisi = bAcisiParam.degeri || 0;
      }

      // C açısı parametresini oku
      const cAcisiParam = this.wtecSahne.CAcisi?.userData?.['FikoParametreler']?.[0];
      if (cAcisiParam) {
        this.cAcisi = cAcisiParam.degeri || 0;
      }
    } catch (err) {
    }
  }

  /**
   * Değer değiştiğinde (I, J, K)
   */
  degerDegisti(): void {
    this.guncelleAciSapmasi();
    this.yenile();
  }

  /**
   * Açı değiştiğinde
   */
  aciDegisti(): void {
    this.aciUygula();
    this.guncelleAciSapmasi();
    this.yenile();
  }

  /**
   * Açıları WTEC modele uygula
   */
  private aciUygula(): void {
    if (!this.wtecSahne) return;

    try {
      // B açısını uygula
      const bAcisiParam = this.wtecSahne.BAcisi?.userData?.['FikoParametreler']?.[0];
      if (bAcisiParam && typeof bAcisiParam.degisti === 'function') {
        bAcisiParam.degeri = this.bAcisi;
        bAcisiParam.degisti(this.bAcisi);
      }

      // C açısını uygula
      const cAcisiParam = this.wtecSahne.CAcisi?.userData?.['FikoParametreler']?.[0];
      if (cAcisiParam && typeof cAcisiParam.degisti === 'function') {
        cAcisiParam.degeri = this.cAcisi;
        cAcisiParam.degisti(this.cAcisi);
      }

      // Render
      this.threeJs?.FikoYT?.degisti?.emit();
    } catch (err) {
    }
  }

  /**
   * I, J, K'dan açı hesapla
   */
  private aciHesapla(): void {
    const i = this.iDeger;
    const j = this.jDeger;
    const k = this.kDeger;

    // Basit vektör açı hesabı
    const length = Math.sqrt(i * i + j * j + k * k);
    if (length > 0) {
      this.bAcisi = THREE.MathUtils.radToDeg(Math.acos(k / length));
      this.cAcisi = THREE.MathUtils.radToDeg(Math.atan2(j, i));
      if (this.cAcisi < 0) this.cAcisi += 360;
    }
  }

  /**
   * Değer al butonu
   */
  degerAl(eksen: string): void {
    // ${eksen} değeri alınıyor...
    // Burada ana 3D sahneden değer alınabilir
  }

  /**
   * Kaçma değerleri - Takımın kaçma miktarlarını hesaplar
   */
  kacmaDegerleri(): void {
    // Kaçma değerleri hesaplanıyor...
    // TODO: ConvexHull ile kaçma değerleri hesaplanabilir
  }

  /**
   * Açısal kaydırma
   */
  acisalKaydirma(): void {
    // Açısal kaydırma uygulanıyor...
  }

  /**
   * I, J, K vektörü ile kafanın mevcut yönü arasındaki açı farkını hesapla
   */
  private farkHesapla(): number {
    if (!this.wtecSahne?.CAcisi) return 180;

    // Kafanın mevcut yönünü al
    const CWorldDir = new THREE.Vector3();
    this.wtecSahne.CAcisi.getWorldDirection(CWorldDir);
    CWorldDir.normalize();

    // Hedef yön vektörü (I, J, K)
    const hedefYon = new THREE.Vector3(this.iDeger, this.jDeger, this.kDeger);
    hedefYon.normalize();

    // İki vektör arasındaki açıyı hesapla
    const angleRadians = CWorldDir.angleTo(hedefYon);

    // Radyandan dereceye çevir
    return THREE.MathUtils.radToDeg(angleRadians);
  }

  /**
   * Açıyı bul - WebGPU hızlandırılmış veya CPU fallback
   * GPU varsa: ~5ms içinde tüm 360x360 kombinasyonu paralel hesaplar
   * GPU yoksa: Adaptive CPU algoritması kullanır
   */
  async aciyiBul(): Promise<void> {
    if (this.aciHesaplaniyor) return;
    if (!this.wtecSahne?.BAcisi || !this.wtecSahne?.CAcisi) {
      return;
    }

    // K < 0 kontrolü - fiziksel sınırlama
    if (this.kDeger < 0) {
      return;
    }

    this.aciHesaplaniyor = true;
    this.cd.detectChanges();

    try {
      // Özel durumlar - doğrudan açıya git
      if (this.iDeger === 0 && this.kDeger === 0 && this.jDeger > 0) {
        await this.aciyaGit(270, 180);
        return;
      }

      if (this.iDeger === 0 && this.kDeger === 0 && this.jDeger < 0) {
        await this.aciyaGit(90, 180);
        return;
      }

      if (this.kDeger === 0 && this.jDeger === 0 && this.iDeger > 0) {
        await this.aciyaGit(180, 180);
        return;
      }

      if (this.kDeger === 0 && this.jDeger === 0 && this.iDeger < 0) {
        await this.aciyaGit(0, 180);
        return;
      }

      // Mevcut açıları kaydet - null kontrolü ile
      const bParam = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0];
      const cParam = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0];

      if (!bParam || !cParam) {
        return;
      }

      const temp_BAcisi = bParam.degeri || 0;
      const temp_CAcisi = cParam.degeri || 0;

      // ═══════════════════════════════════════════════════════════════
      // WebGPU ile hızlı arama dene (Diagonal Kafa için özel shader)
      // ═══════════════════════════════════════════════════════════════
      const gpuAvailable = await this.gpuAngleSearch.isAvailable();

      if (gpuAvailable) {

        // Kalibrasyon değerlerini al
        const bKalibrasyon = this.wtecSahne.BAcisi?.kalibrasyon ?? -90;
        const cKalibrasyon = this.wtecSahne.CAcisi?.kalibrasyon ?? 0;

        // GPU ile çok aşamalı hassas arama (Diagonal kinematik modeli)
        const result = await this.gpuAngleSearch.zeroSearchDiagonal({
          targetI: this.iDeger,
          targetJ: this.jDeger,
          targetK: this.kDeger,
          bMin: 0,
          bMax: 360,
          cMin: 0,
          cMax: 360,
          bKalibrasyon,
          cKalibrasyon
        });

        if (result) {

          // ═══════════════════════════════════════════════════════════════
          // GPU + CPU HİBRİT: GPU sonucunu CPU ile doğrula ve 0'a ulaş
          // GPU hızlı yaklaşık sonuç verir, CPU tam 0'a ulaştırır
          // ═══════════════════════════════════════════════════════════════
          let finalB = result.bestB;
          let finalC = result.bestC;

          // GPU sonucunu uygula ve gerçek farkı kontrol et
          bParam.degeri = finalB;
          bParam.degisti(finalB);
          cParam.degeri = finalC;
          cParam.degisti(finalC);

          let gercekFark = this.farkHesaplaSessiz();

          // Eğer gerçek fark > 0 ise CPU ile son ince ayar yap - TAM 0'A ULAŞMAK İÇİN
          if (gercekFark > 0.000001) {

            // Ultra hassas lokal arama - TAM 0'A ULAŞMAK İÇİN DAHA KÜCÜK ADIMLAR
            const hassasAdimlar = [0.01, 0.001, 0.0001, 0.00001, 0.000001];

            for (const adim of hassasAdimlar) {
              if (gercekFark < 0.000001) break; // Neredeyse 0

              const aralik = adim * 50;
              let enIyiFark = gercekFark;

              // C açısı lokal arama
              const cMin = Math.max(0, finalC - aralik);
              const cMax = Math.min(360, finalC + aralik);
              for (let c = cMin; c <= cMax; c += adim) {
                cParam.degeri = c;
                cParam.degisti(c);
                const fark = this.farkHesaplaSessiz();
                if (fark < enIyiFark) {
                  enIyiFark = fark;
                  finalC = c;
                }
              }
              cParam.degeri = finalC;
              cParam.degisti(finalC);

              // B açısı lokal arama
              const bMin = Math.max(0, finalB - aralik);
              const bMax = Math.min(360, finalB + aralik);
              for (let b = bMin; b <= bMax; b += adim) {
                bParam.degeri = b;
                bParam.degisti(b);
                const fark = this.farkHesaplaSessiz();
                if (fark < enIyiFark) {
                  enIyiFark = fark;
                  finalB = b;
                }
              }
              bParam.degeri = finalB;
              bParam.degisti(finalB);

              gercekFark = enIyiFark;
            }
          }

          // Eski değerlere geri dön (animasyon için)
          bParam.degeri = temp_BAcisi;
          bParam.degisti(temp_BAcisi);
          cParam.degeri = temp_CAcisi;
          cParam.degisti(temp_CAcisi);

          // Animasyonlu geçiş
          await this.aciyaGit(finalB, finalC, temp_BAcisi, temp_CAcisi);
          return;
        }
      }

      // ═══════════════════════════════════════════════════════════════
      // CPU Fallback - Adaptive algoritma
      // ═══════════════════════════════════════════════════════════════
      const sonuc = await this.aciBulAlgoritmasi();

      // Bulunan açılara animasyonlu geçiş yap
      await this.aciyaGit(sonuc.b, sonuc.c, temp_BAcisi, temp_CAcisi);

    } finally {
      this.aciHesaplaniyor = false;
      this.cd.detectChanges();
    }
  }

  /**
   * Çok aşamalı açı bulma algoritması
   * Açı sapması 0 olana kadar ara (GPU ile aynı hassasiyet)
   */
  private async aciBulAlgoritmasi(): Promise<{ b: number; c: number }> {
    let b_en_kucuk = 0;
    let c_en_kucuk = 0;
    let iterasyon = 0;
    const maxIterasyon = 50; // Daha fazla iterasyon - 0'a ulaşmak için

    // AŞAMA 1: Kaba arama (1° adım ile hızlı tarama)
    await this.aramaYap(1, (b, c) => {
      b_en_kucuk = b;
      c_en_kucuk = c;
    });

    let mevcutFark = this.farkHesapla();

    // Fark toleransın altındaysa direkt dön
    if (mevcutFark < this.farkTolerans) {
      return { b: b_en_kucuk, c: c_en_kucuk };
    }

    // AŞAMA 2: fark > 1 ise TAM 360° tarama (0.5° adım)
    if (mevcutFark > 1) {
      await this.aramaYap(0.5, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 3: fark > 0.1 ise 0.1° adım
    if (mevcutFark > 0.1) {
      await this.aramaYap(0.1, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 4: fark > 0.01 ise 0.01° adım
    if (mevcutFark > 0.01) {
      await this.aramaYap(0.01, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 5: ULTRA HASSAS LOKAL ARAMA - 0'a ulaşana kadar
    // Adım boyutu: 0.001° → 0.0001° → 0.00001°
    const hassasAdimlar = [0.001, 0.0001, 0.00001, 0.000001];

    for (const adim of hassasAdimlar) {
      if (mevcutFark < this.farkTolerans) break;

      iterasyon = 0;
      while (mevcutFark >= this.farkTolerans && iterasyon < maxIterasyon) {
        iterasyon++;

        // Dinamik aralık: küçük adımda küçük aralık
        const aralik = Math.max(adim * 100, 0.01);

        await this.inceAramaYapLokal(b_en_kucuk, c_en_kucuk, adim, aralik, (b, c) => {
          b_en_kucuk = b;
          c_en_kucuk = c;
        });

        const yeniFark = this.farkHesapla();

        // İyileşme yoksa bir sonraki adıma geç
        if (Math.abs(yeniFark - mevcutFark) < adim * 0.1) {
          break;
        }

        mevcutFark = yeniFark;
      }
    }

    return { b: b_en_kucuk, c: c_en_kucuk };
  }

  /**
   * Belirli adım ile tam arama yap (sessiz - 3D güncelleme yok)
   * Sadece degeri değiştirir, degisti() çağırmaz - 3D model hareket etmez
   */
  private aramaYap(adim: number, sonucCallback: (b: number, c: number) => void): Promise<void> {
    return new Promise((resolve) => {
      // Null kontrolü
      const cParam = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0];
      const bParam = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0];

      if (!cParam || !bParam || typeof cParam.degisti !== 'function' || typeof bParam.degisti !== 'function') {
        sonucCallback(0, 0);
        resolve();
        return;
      }

      // Mevcut değerleri kaydet
      const eskiB = bParam.degeri;
      const eskiC = cParam.degeri;

      // setTimeout ile UI thread'i serbest bırak
      setTimeout(() => {
        let b_en_kucuk = 0;
        let c_en_kucuk = 0;
        let aci_onceki_fark = 180;

        // C açısını ara (sessiz - sadece degeri değiştir)
        for (let i = 0; i <= 360; i += adim) {
          cParam.degeri = i;
          cParam.degisti(i); // Matrix güncellemesi için gerekli ama render yok
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            c_en_kucuk = i;
          }
        }

        cParam.degeri = c_en_kucuk;
        cParam.degisti(c_en_kucuk);

        // B açısını ara (sessiz)
        aci_onceki_fark = 180;
        for (let j = 0; j <= 360; j += adim) {
          bParam.degeri = j;
          bParam.degisti(j);
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            b_en_kucuk = j;
          }
        }

        // KRİTİK FİX: Aramadan sonra ESKİ değerlere GERİ DÖN (eski versiyon mantığı)
        // Bulunan değerler callback'e gönderilir ama pozisyon eski değere resetlenir
        // aciyaGit() fonksiyonu eski değerden bulunan değere animasyon yapar
        bParam.degeri = eskiB;
        bParam.degisti(eskiB);
        cParam.degeri = eskiC;
        cParam.degisti(eskiC);

        sonucCallback(b_en_kucuk, c_en_kucuk);
        resolve();
      }, 0);
    });
  }

  /**
   * Sessiz fark hesapla - render tetiklemez
   * PERFORMANS: Reusable Vector3 objeler kullanır
   */
  private farkHesaplaSessiz(): number {
    if (!this.wtecSahne?.CAcisi) return 180;

    // Matrix'i güncelle (render olmadan)
    this.wtecSahne.CAcisi.updateMatrixWorld(true);

    // PERFORMANS: Reusable Vector3 - YENİ OBJE YARATMA YOK
    this.wtecSahne.CAcisi.getWorldDirection(this._tempCWorldDir);
    this._tempCWorldDir.normalize();

    this._tempHedefYon.set(this.iDeger, this.jDeger, this.kDeger);
    this._tempHedefYon.normalize();

    const angleRadians = this._tempCWorldDir.angleTo(this._tempHedefYon);
    return THREE.MathUtils.radToDeg(angleRadians);
  }

  /**
   * Bulunan değer etrafında ince arama yap (sessiz - 3D güncelleme yok)
   */
  private inceAramaYap(
    bMerkez: number,
    cMerkez: number,
    adim: number,
    aralik: number,
    sonucCallback: (b: number, c: number) => void
  ): Promise<void> {
    return new Promise((resolve) => {
      // Null kontrolü
      const cParam = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0];
      const bParam = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0];

      if (!cParam || !bParam || typeof cParam.degisti !== 'function' || typeof bParam.degisti !== 'function') {
        sonucCallback(bMerkez, cMerkez);
        resolve();
        return;
      }

      // Mevcut değerleri kaydet
      const eskiB = bParam.degeri;
      const eskiC = cParam.degeri;

      // setTimeout ile UI thread'i serbest bırak
      setTimeout(() => {
        let b_en_kucuk = bMerkez;
        let c_en_kucuk = cMerkez;

        // Önce merkez değerlere git (sessiz)
        cParam.degeri = cMerkez;
        cParam.degisti(cMerkez);
        bParam.degeri = bMerkez;
        bParam.degisti(bMerkez);

        let aci_onceki_fark = this.farkHesaplaSessiz();

        // C açısı TAM 360 DERECE tarama (tam versiyon - yavaş ama garantili)
        for (let i = 0; i <= 360; i += adim) {
          cParam.degeri = i;
          cParam.degisti(i);
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            c_en_kucuk = i;
          }
        }

        cParam.degeri = c_en_kucuk;
        cParam.degisti(c_en_kucuk);

        // B açısı TAM 360 DERECE tarama
        aci_onceki_fark = this.farkHesaplaSessiz();

        for (let j = 0; j <= 360; j += adim) {
          bParam.degeri = j;
          bParam.degisti(j);
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            b_en_kucuk = j;
          }
        }

        // KRİTİK FİX: Aramadan sonra ESKİ değerlere GERİ DÖN (eski versiyon mantığı)
        // Bulunan değerler callback'e gönderilir ama pozisyon eski değere resetlenir
        // aciyaGit() fonksiyonu eski değerden bulunan değere animasyon yapar
        bParam.degeri = eskiB;
        bParam.degisti(eskiB);
        cParam.degeri = eskiC;
        cParam.degisti(eskiC);

        sonucCallback(b_en_kucuk, c_en_kucuk);
        resolve();
      }, 0);
    });
  }

  /**
   * Belirtilen B ve C açılarına animasyonlu geçiş yap
   */
  private aciyaGit(
    bAcisina: number,
    cAcisina: number,
    bAcisindan: number = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0]?.degeri || 0,
    cAcisindan: number = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0]?.degeri || 0
  ): Promise<void> {
    return new Promise((resolve) => {
      // Null kontrolü
      const bParam = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0];
      const cParam = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0];

      if (!bParam || !cParam || typeof bParam.degisti !== 'function' || typeof cParam.degisti !== 'function') {
        this.aciHesaplaniyor = false;
        resolve();
        return;
      }

      bParam.degeri = bAcisindan;
      bParam.degisti(bAcisindan);
      cParam.degeri = cAcisindan;
      cParam.degisti(cAcisindan);

      // Sürekli çizim başlat
      this.threeJs?.surekliCiz?.();

      // TWEEN ile animasyonlu geçiş
      const TWEEN = (window as any).TWEEN;
      if (TWEEN) {
        // B açısı animasyonu
        const bAnim = new TWEEN.Tween(bParam)
          .to({ degeri: bAcisina }, 1000)
          .onUpdate(() => {
            bParam.degisti(bParam.degeri);
            this.bAcisi = bParam.degeri;
          })
          .easing(TWEEN.Easing.Quadratic.Out);

        // C açısı animasyonu (750ms gecikme ile)
        const cAnim = new TWEEN.Tween(cParam)
          .delay(750)
          .to({ degeri: cAcisina }, 1000)
          .onUpdate(() => {
            cParam.degisti(cParam.degeri);
            this.cAcisi = cParam.degeri;
          })
          .easing(TWEEN.Easing.Quadratic.Out)
          .onComplete(() => {
            this.aciHesaplaniyor = false;
            this.aciSapmasi = this.farkHesapla();
            this.threeJs?.surekliCizIptal?.();
            this.cd.detectChanges();
            resolve();
          });

        bAnim.start();
        cAnim.start();
      } else {
        // TWEEN yoksa direkt uygula
        bParam.degeri = bAcisina;
        bParam.degisti(bAcisina);
        cParam.degeri = cAcisina;
        cParam.degisti(cAcisina);

        this.bAcisi = bAcisina;
        this.cAcisi = cAcisina;
        this.aciSapmasi = this.farkHesapla();
        this.aciHesaplaniyor = false;

        this.yenile();
        resolve();
      }

      this.cd.detectChanges();
    });
  }

  /**
   * LOKAL ince arama - merkez etrafında ±aralik içinde HIZLI arama
   * TAM 360 derece yerine sadece lokal bölgede arar (ÇOOK HIZLI)
   */
  private inceAramaYapLokal(
    bMerkez: number,
    cMerkez: number,
    adim: number,
    aralik: number,
    sonucCallback: (b: number, c: number) => void
  ): Promise<void> {
    return new Promise((resolve) => {
      const cParam = this.wtecSahne?.CAcisi?.userData?.['FikoParametreler']?.[0];
      const bParam = this.wtecSahne?.BAcisi?.userData?.['FikoParametreler']?.[0];

      if (!cParam || !bParam || typeof cParam.degisti !== 'function' || typeof bParam.degisti !== 'function') {
        sonucCallback(bMerkez, cMerkez);
        resolve();
        return;
      }

      const eskiB = bParam.degeri;
      const eskiC = cParam.degeri;

      setTimeout(() => {
        let b_en_kucuk = bMerkez;
        let c_en_kucuk = cMerkez;

        // Merkez değerlere git
        cParam.degeri = cMerkez;
        cParam.degisti(cMerkez);
        bParam.degeri = bMerkez;
        bParam.degisti(bMerkez);

        let aci_onceki_fark = this.farkHesaplaSessiz();

        // C açısı LOKAL arama (±aralik içinde)
        const cMin = Math.max(0, cMerkez - aralik);
        const cMax = Math.min(360, cMerkez + aralik);

        for (let i = cMin; i <= cMax; i += adim) {
          cParam.degeri = i;
          cParam.degisti(i);
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            c_en_kucuk = i;
          }
        }

        cParam.degeri = c_en_kucuk;
        cParam.degisti(c_en_kucuk);

        // B açısı LOKAL arama (±aralik içinde)
        const bMin = Math.max(0, bMerkez - aralik);
        const bMax = Math.min(360, bMerkez + aralik);
        aci_onceki_fark = this.farkHesaplaSessiz();

        for (let j = bMin; j <= bMax; j += adim) {
          bParam.degeri = j;
          bParam.degisti(j);
          const fark = this.farkHesaplaSessiz();
          if (fark < aci_onceki_fark) {
            aci_onceki_fark = fark;
            b_en_kucuk = j;
          }
        }

        // ESKİ değerlere geri dön (animasyon için)
        bParam.degeri = eskiB;
        bParam.degisti(eskiB);
        cParam.degeri = eskiC;
        cParam.degisti(eskiC);

        sonucCallback(b_en_kucuk, c_en_kucuk);
        resolve();
      }, 0);
    });
  }

  /**
   * Açı değiştiğinde sapma değerini güncelle
   */
  private guncelleAciSapmasi(): void {
    // wtecSahne hazır değilse çıkış
    if (!this.wtecSahne?.CAcisi) return;
    this.aciSapmasi = this.farkHesapla();
  }

  override temizle(): void {
    // WTEC Kafa temizlik için özel işlem gerekmiyor
    // FikoThreeJsComponent otomatik temizler
  }
}
